import React, { useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Dialog,
} from "@material-ui/core";
import {
  GetApp as DownloadIcon,
  FolderOpen as FolderIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  ExpandMore as ExpandMoreIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
} from "@material-ui/icons";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import ModalImageCors from "../ModalImageCors";

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    display: "grid",
    gap: 4,
    borderRadius: 8,
    overflow: "hidden",
    maxWidth: 500,
    margin: "4px 0",
  },
  grid4col: {
    gridTemplateColumns: "repeat(4, 1fr)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  gridItem: {
    position: "relative",
    paddingBottom: "100%", // Quadrado
    overflow: "hidden",
    cursor: "pointer",
    backgroundColor: "#e0e0e0",
    "&:hover $gridOverlay": {
      opacity: 1,
    },
    "&:hover $menuButton": {
      opacity: 1,
    },
  },
  menuButton: {
    position: "absolute",
    top: 4,
    right: 4,
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.4)",
    opacity: 0,
    transition: "opacity 0.2s ease",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.6)",
    },
    padding: 2,
  },
  gridTimestamp: {
    position: "absolute",
    bottom: 4,
    right: 6,
    color: "#fff",
    fontSize: "0.65rem",
    textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
    zIndex: 10,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  gridImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  gridOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.3)",
    opacity: 0,
    transition: "opacity 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 8px",
    borderRadius: "8px 8px 0 0",
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
  },
  toolbarInfo: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  imageCount: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  timestamp: {
    fontSize: "0.65rem",
    color: theme.palette.text.hint,
  },
  lightboxOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.9)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lightboxImage: {
    maxWidth: "90vw",
    maxHeight: "85vh",
    objectFit: "contain",
    borderRadius: 4,
  },
  lightboxNav: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.15)",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.3)",
    },
  },
  lightboxToolbar: {
    position: "absolute",
    top: 16,
    right: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 10001,
    padding: "5px 7px",
    borderRadius: 30,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.35)",
  },
  lightboxToolbarBtn: {
    width: 44,
    height: 44,
    padding: 0,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: "50%",
    color: "#fff",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "scale(1.08)",
    },
    "& svg": {
      width: 22,
      height: 22,
      fill: "#fff",
    },
  },
  lightboxCloseBtn: {
    backgroundColor: "rgba(255, 0, 0, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(255, 0, 0, 1)",
      transform: "scale(1.1)",
    }
  },
  lightboxCounter: {
    position: "absolute",
    bottom: 20,
    color: "#fff",
    fontSize: "0.9rem",
  },
  checkboxContainer: {
    position: "absolute",
    top: 4,
    left: 4,
    zIndex: 11,
    backgroundColor: "rgba(255,255,255,0.7)",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const ImageGrid = ({ messages, fromMe, onOpenOptionsMenu, renderMessageAck, showSelectMessageCheckbox }) => {
  const classes = useStyles();
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [downloading, setDownloading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  const openLightbox = useCallback((idx) => {
    setLightboxIndex(idx);
    setRotation(0);
    setScale(1);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(-1);
    setRotation(0);
    setScale(1);
  }, []);

  const navPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : messages.length - 1));
    setRotation(0);
    setScale(1);
  }, [messages.length]);

  const navNext = useCallback(() => {
    setLightboxIndex((prev) => (prev < messages.length - 1 ? prev + 1 : 0));
    setRotation(0);
    setScale(1);
  }, [messages.length]);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (!document.fullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(err => {
          console.error("Erro ao ativar tela cheia:", err);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, []);

  const handleDownloadSingle = useCallback(async () => {
    const msg = messages[lightboxIndex];
    if (!msg) return;
    try {
      const response = await fetch(msg.mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", msg.mediaName || `imagem_${lightboxIndex + 1}.jpg`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao baixar:", err);
    }
  }, [lightboxIndex, messages]);

  // Keyboard nav no lightbox
  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft") navPrev();
    else if (e.key === "ArrowRight") navNext();
    else if (e.key === "Escape") closeLightbox();
  }, [navPrev, navNext, closeLightbox]);

  // Download ZIP
  const handleDownloadZip = useCallback(async () => {
    setDownloading(true);
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      const nameCount = {};
      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        try {
          const response = await fetch(msg.mediaUrl);
          if (!response.ok) continue;
          const blob = await response.blob();

          let name = msg.mediaName || `imagem_${i + 1}.jpg`;
          if (nameCount[name]) {
            const ext = name.lastIndexOf(".");
            const base = ext > 0 ? name.substring(0, ext) : name;
            const extension = ext > 0 ? name.substring(ext) : ".jpg";
            nameCount[name]++;
            name = `${base}_${nameCount[name]}${extension}`;
          } else {
            nameCount[name] = 1;
          }

          zip.file(name, blob);
        } catch (err) {
          console.warn(`Erro ao baixar imagem ${i}:`, err);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = `imagens_${format(parseISO(messages[0].createdAt), "yyyy-MM-dd_HHmm")}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao gerar ZIP:", err);
    }
    setDownloading(false);
  }, [messages]);

  // Salvar em pasta (File System Access API)
  const handleSaveToFolder = useCallback(async () => {
    try {
      const dirHandle = await window.showDirectoryPicker();
      let saved = 0;

      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        try {
          const response = await fetch(msg.mediaUrl);
          if (!response.ok) continue;
          const blob = await response.blob();
          const name = msg.mediaName || `imagem_${i + 1}.jpg`;
          const fileHandle = await dirHandle.getFileHandle(name, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          saved++;
        } catch (err) {
          console.warn(`Erro ao salvar imagem ${i}:`, err);
        }
      }

      if (saved > 0) {
        console.log(`${saved} imagens salvas na pasta`);
      }
    } catch (err) {
      // Usuário cancelou ou API não suportada
      if (err.name !== "AbortError") {
        console.error("Erro ao salvar em pasta:", err);
      }
    }
  }, [messages]);

  const supportsDirectoryPicker = typeof window.showDirectoryPicker === "function";

  const firstTime = messages[0]?.createdAt;
  const lastTime = messages[messages.length - 1]?.createdAt;

  return (
    <Box>
      {/* Toolbar */}
      <div className={classes.toolbar}>
        <div className={classes.toolbarInfo}>
          <Typography className={classes.imageCount}>
            {messages.length} imagens
          </Typography>
          {firstTime && (
            <Typography className={classes.timestamp}>
              {format(parseISO(firstTime), "HH:mm")}
              {lastTime && lastTime !== firstTime && ` — ${format(parseISO(lastTime), "HH:mm")}`}
            </Typography>
          )}
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          <Tooltip title="Baixar todas (ZIP)">
            <IconButton size="small" onClick={handleDownloadZip} disabled={downloading}>
              {downloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {supportsDirectoryPicker && (
            <Tooltip title="Salvar em pasta">
              <IconButton size="small" onClick={handleSaveToFolder}>
                <FolderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className={`${classes.gridContainer} ${classes.grid4col}`}>
        {messages.map((msg, idx) => (
          <div key={msg.id} className={classes.gridItem} onClick={() => openLightbox(idx)}>
            <img
              src={msg.mediaUrl}
              alt={msg.mediaName || `Imagem ${idx + 1}`}
              className={classes.gridImage}
              loading="lazy"
            />
            <div className={classes.gridOverlay} />

            {showSelectMessageCheckbox && (
              <div className={classes.checkboxContainer} onClick={(e) => e.stopPropagation()}>
                <SelectMessageCheckbox message={msg} />
              </div>
            )}

            {onOpenOptionsMenu && !showSelectMessageCheckbox && (
              <IconButton
                size="small"
                className={classes.menuButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenOptionsMenu(e, msg);
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}

            <div className={classes.gridTimestamp}>
              {format(parseISO(msg.createdAt), "HH:mm")}
              {/* ack só faz sentido em mensagens enviadas; recebidas têm ack=0 e mostrariam relógio indevido */}
              {renderMessageAck && msg.fromMe && renderMessageAck(msg)}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIndex >= 0 && (
        <Dialog
          open
          fullScreen
          onClose={closeLightbox}
          PaperProps={{ style: { backgroundColor: "rgba(0,0,0,0.95)" } }}
          onKeyDown={handleKeyDown}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <IconButton
              className={classes.lightboxNav}
              style={{ left: 16 }}
              onClick={navPrev}
            >
              <ChevronLeft style={{ fontSize: 40 }} />
            </IconButton>

            <div className={classes.lightboxToolbar}>
              <IconButton className={classes.lightboxToolbarBtn} onClick={handleDownloadSingle} title="Baixar Imagem">
                <DownloadIcon />
              </IconButton>
              <IconButton className={classes.lightboxToolbarBtn} onClick={handleFullscreen} title="Tela Cheia">
                <FullscreenIcon />
              </IconButton>
              <IconButton className={classes.lightboxToolbarBtn} onClick={handleRotate} title="Rotacionar">
                <RotateRightIcon />
              </IconButton>
              <IconButton className={classes.lightboxToolbarBtn} onClick={handleZoomIn} title="Mais Zoom">
                <ZoomInIcon />
              </IconButton>
              <IconButton className={classes.lightboxToolbarBtn} onClick={handleZoomOut} title="Menos Zoom">
                <ZoomOutIcon />
              </IconButton>
              <IconButton className={`${classes.lightboxToolbarBtn} ${classes.lightboxCloseBtn}`} onClick={closeLightbox} title="Fechar">
                <CloseIcon />
              </IconButton>
            </div>

            <img
              src={messages[lightboxIndex]?.mediaUrl}
              alt=""
              className={classes.lightboxImage}
              style={{ transform: `rotate(${rotation}deg) scale(${scale})`, transition: "transform 0.3s ease" }}
            />

            <IconButton
              className={classes.lightboxNav}
              style={{ right: 16 }}
              onClick={navNext}
            >
              <ChevronRight style={{ fontSize: 40 }} />
            </IconButton>

            <Typography className={classes.lightboxCounter}>
              {lightboxIndex + 1} / {messages.length}
            </Typography>
          </div>
        </Dialog>
      )}
    </Box>
  );
};

export default React.memo(ImageGrid);
